import localForage from 'localforage';

// SEE: https://localforage.github.io/localForage/

let getSerializerPromiseCache;

function isStorageUsable(storage = null) {
    if (!storage) {
        return false;
    }

    const storageTestKey = '_localforage_support_test';

    try {
        storage.setItem(storageTestKey, 'true');
        storage.removeItem(storageTestKey);
        return true;
    } catch (e) {
        return storage.length > 0;
    }
}

function getKeyPrefix(options, defaultConfig) {
    let keyPrefix = options.name + '/';
    if (options.storeName !== defaultConfig.storeName) {
        keyPrefix += options.storeName + '/';
    }
    return keyPrefix;
}

function getSerializerPromise(localForageInstance) {
    if (getSerializerPromiseCache) {
        return getSerializerPromiseCache;
    }
    if (!localForageInstance ||
        typeof localForageInstance.getSerializer !== 'function') {
        return Promise.reject(new Error('localforage.getSerializer() was not available! ' +
            'localforage v1.4+ is required!'));
    }
    getSerializerPromiseCache = localForageInstance.getSerializer();
    return getSerializerPromiseCache;
}

function normalizeKey(key) {
    return (typeof key === 'string') ? key : String(key);
}

function executeCallback(promise, callback) {
    if (callback) {
        promise.then(function (result) {
            callback(null, result);
        }, function (error) {
            callback(error);
        });
    }

    return promise;
}

const sessionStorageWrapper = {
    _driver: 'sessionStorageWrapper',

    _support: !!(typeof sessionStorage !== 'undefined' && 'setItem' in sessionStorage && sessionStorage.setItem),

    _initStorage(options = {}) {
        const db = sessionStorage;
        if (!isStorageUsable(db)) return Promise.reject();

        const keyPrefix = getKeyPrefix(options, this._defaultConfig);
        const dbInfo = { ...options, db, keyPrefix };

        this._dbInfo = dbInfo;
        return getSerializerPromise(this).then(serializer => dbInfo.serializer = serializer);
    },

    iterate(iterator, callback) {
        const promise = this.ready().then(() => {
            const { db: store, keyPrefix, serializer } = this._dbInfo;
            const keys = Object.keys(store).filter(k => k.startsWith(keyPrefix));

            let i = 1;

            for (const key of keys) {
                const value = store.getItem(key);
                const deserialized = value ? serializer.deserialize(value) : null;
                const result = iterator(deserialized, key.slice(keyPrefix.length), i++);
                if (result !== void 0) return result;
            }
        });

        executeCallback(promise, callback);
        return promise;
    },

    getItem(key, callback) {
        key = normalizeKey(key);
        const promise = this.ready().then(() => {
            const { db: store, keyPrefix, serializer } = this._dbInfo;
            const value = store.getItem(keyPrefix + key);
            return value ? serializer.deserialize(value) : value;
        });

        executeCallback(promise, callback);
        return promise;
    },

    setItem(key, value, callback) {
        key = normalizeKey(key);
        const promise = this.ready().then(() => {
            const { db: store, keyPrefix, serializer } = this._dbInfo;

            return new Promise((resolve, reject) => {
                serializer.serialize(value ?? null, (val, err) => {
                    if (err) {
                        return reject(err);
                    }

                    try {
                        store.setItem(keyPrefix + key, val);
                        resolve(value);
                    } catch (e) {
                        reject(e);
                    }
                });
            });
        });

        executeCallback(promise, callback);
        return promise;
    },

    removeItem(key, callback) {
        key = normalizeKey(key);
        const promise = this.ready().then(() => {
            const { db: store, keyPrefix } = this._dbInfo;
            store.removeItem(keyPrefix + key);
        });
        
        executeCallback(promise, callback);
        return promise;
    },

    clear(callback) {
        const promise = this.ready().then(() => {
            const { db: store, keyPrefix } = this._dbInfo;
            Object.keys(store).forEach(key => key.startsWith(keyPrefix) && store.removeItem(key));
        });

        executeCallback(promise, callback);
        return promise;
    },

    length(callback) {
        const promise = this.keys().then(keys => keys.length);

        executeCallback(promise, callback);
        return promise;
    },

    key(index, callback) {
        const promise = this.ready().then(() => {
            const { db: store, keyPrefix } = this._dbInfo;
            const key = store.key(index);

            return key?.startsWith(keyPrefix) ? key.slice(keyPrefix.length) : null;
        });

        executeCallback(promise, callback);
        return promise;
    },

    keys(callback) {
        const promise = this.ready().then(() => {
            const { db: store, keyPrefix } = this._dbInfo;

            return Object.keys(store).filter(k => k.startsWith(keyPrefix)).map(k => k.slice(keyPrefix.length));
        });

        executeCallback(promise, callback);
        return promise;
    },

    dropInstance(options = {}, callback) {
        if (typeof options === 'function') {
            callback = options;
            options = {};
        }

        const promise = this.ready().then(() => {
            const { db: store } = this._dbInfo;
            const config = { ...this.config(), ...options };
            const keyPrefix = getKeyPrefix(config, this._defaultConfig);
            
            Object.keys(store).forEach(key => key.startsWith(keyPrefix) && store.removeItem(key));
        }).catch(e => Promise.reject(e));

        executeCallback(promise, callback);
        return promise;
    }
};

localForage.defineDriver(sessionStorageWrapper);

const sessionStore = localForage.createInstance({
    name: 'ehi',
    storeName: 'sessionStore',
    driver: [sessionStorageWrapper._driver]
});

export default sessionStore;